import React from 'react';
import { ImageOff } from 'lucide-react';
import StepHeading from './StepHeading';

interface Step7Props {
  selectedName: string;
  selectedPrice: number;
  category: string;
  shopifyUrl: string;
  previewUrl?: string;
  productImage?: string;
}

const toHandle = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const Step7: React.FC<Step7Props> = ({
  selectedName,
  selectedPrice,
  category,
  shopifyUrl,
  previewUrl,
  productImage,
}) => {
  const isError = /error/i.test(shopifyUrl);

  const liveUrl = (() => {
    if (previewUrl) return previewUrl;
    if (isError) return '';
    return `https://superpossible.myshopify.com/products/${toHandle(selectedName)}`;
  })();

  return (
    <div className="ed-enter">
      <StepHeading
        index={7}
        kicker={isError ? 'Not published' : 'Published'}
        title={isError ? 'We could not publish this one.' : selectedName}
        accent={isError ? undefined : 'is live.'}
        lede={
          isError
            ? 'Shopify rejected the publish request. The response is below — try again once it is resolved.'
            : 'Your product now has a name, a face, a story, and a price on the SuperPossible storefront.'
        }
      />

      {isError ? (
        <div className="ed-note ed-note--warn">
          <span className="ed-kicker">Shopify response</span>
          <code>{shopifyUrl}</code>
        </div>
      ) : (
        <div className="ed-final ed-final--split">
          <div className="ed-final-media">
            {productImage ? (
              <img src={productImage} alt={selectedName} />
            ) : (
              <div className="ed-final-placeholder" aria-hidden="true">
                <ImageOff />
              </div>
            )}
          </div>

          <div className="ed-final-body">
            <span className="ed-badge">Live on SuperPossible</span>
            <p className="ed-final-price">${Number(selectedPrice).toFixed(2)}</p>

            <div className="ed-spec">
              <div className="ed-spec-row">
                <span className="ed-spec-key">Product</span>
                <span className="ed-spec-val">{selectedName}</span>
              </div>
              <div className="ed-spec-row">
                <span className="ed-spec-key">Category</span>
                <span className="ed-spec-val">{category}</span>
              </div>
              <div className="ed-spec-row">
                <span className="ed-spec-key">Storefront</span>
                <span className="ed-spec-val">SuperPossible</span>
              </div>
            </div>

            <div className="ed-actions">
              {liveUrl && (
                <a
                  className="ed-btn ed-btn--primary ed-btn--lg"
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View live product
                </a>
              )}
              <button
                type="button"
                className="ed-btn ed-btn--lg"
                onClick={() => window.location.reload()}
              >
                Start another
              </button>
            </div>
          </div>
        </div>
      )}

      {isError && (
        <div className="ed-actions" style={{ marginTop: 32 }}>
          <button type="button" className="ed-btn ed-btn--lg" onClick={() => window.location.reload()}>
            Start over
          </button>
        </div>
      )}
    </div>
  );
};

export default Step7;
